import React, { useState, useRef, useEffect } from 'react';
import { Send, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface CustomChatWidgetProps {
  agentId?: string;
  initialMessage?: string;
  placeholder?: string;
  className?: string;
}

const CustomChatWidget: React.FC<CustomChatWidgetProps> = ({
  agentId = 'agent_8201kaky1sesezjb36ww3q0ae8e8', // Text-only chat agent (no mic needed)
  initialMessage = "Hi! I'm Sarah's double. I can answer questions about DoubleMe just like Sarah would. What would you like to know?",
  placeholder = "Ask me anything...",
  className = ""
}) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: initialMessage,
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const wsRef = useRef<WebSocket | null>(null);

  // Determine backend URL based on environment
  const getBackendUrl = () => {
    // Check if we're in production (deployed)
    const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';

    if (isProduction) {
      return 'https://doubleme-production.up.railway.app';
    }
    return 'http://localhost:3000';
  };

  const getWebSocketUrl = () => {
    const backendUrl = getBackendUrl();
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace('https://', '').replace('http://', '');
    return `${wsProtocol}://${wsHost}/api/elevenlabs/ws`;
  };

  // Initialize WebSocket connection on mount
  useEffect(() => {
    connectToBackend();

    return () => {
      // Cleanup on unmount
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const connectToBackend = () => {
    try {
      const wsUrl = getWebSocketUrl();
      console.log('Connecting to backend WebSocket:', wsUrl);

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('Connected to backend WebSocket');
        setConnectionError(null);

        // Send initialization message
        ws.send(JSON.stringify({
          type: 'init',
          agentId: agentId
        }));
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log('Received message from backend:', message.type);

          switch (message.type) {
            case 'connected':
              console.log('ElevenLabs connection established');
              setIsConnected(true);
              setConnectionError(null);
              break;

            case 'agent_response':
              if (message.text) {
                const assistantMessage: Message = {
                  id: Date.now().toString(),
                  role: 'assistant',
                  content: message.text,
                  timestamp: new Date()
                };
                setMessages(prev => [...prev, assistantMessage]);
                setIsLoading(false);
              }
              break;

            case 'error':
              console.error('Error from backend:', message.message);
              setConnectionError(message.message || 'Connection error occurred');
              setIsLoading(false);

              const errorMessage: Message = {
                id: Date.now().toString(),
                role: 'assistant',
                content: `I'm having trouble connecting right now. Please try calling me instead at +1 (629) 230-9527.`,
                timestamp: new Date()
              };
              setMessages(prev => [...prev, errorMessage]);
              break;

            case 'disconnected':
              console.log('Disconnected from ElevenLabs');
              setIsConnected(false);
              break;

            case 'pong':
              // Heartbeat response
              break;

            default:
              console.log('Unknown message type:', message.type);
          }
        } catch (error) {
          console.error('Error parsing message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionError('Failed to connect to chat service');
        setIsConnected(false);
      };

      ws.onclose = () => {
        console.log('WebSocket connection closed');
        setIsConnected(false);

        // Attempt to reconnect after 5 seconds
        setTimeout(() => {
          if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
            console.log('Attempting to reconnect...');
            connectToBackend();
          }
        }, 5000);
      };

    } catch (error) {
      console.error('Error connecting to backend:', error);
      setConnectionError('Failed to initialize chat connection');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    if (!isConnected) {
      setConnectionError('Not connected to chat service. Reconnecting...');
      connectToBackend();
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      // Send message through WebSocket
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'user_message',
          text: userMessage.content
        }));
      } else {
        throw new Error('WebSocket is not connected');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setIsLoading(false);

      const errorMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: "I'm having trouble connecting right now. Please try calling me instead at +1 (629) 230-9527.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const suggestedQuestions = [
    "How do you sound exactly like Sarah?",
    "What makes you different from a chatbot?",
    "Can you tell me about pricing?",
    "How long does setup take?"
  ];

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Connection Status Indicator */}
      {connectionError && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-2">
          <p className="text-xs text-red-600">{connectionError}</p>
        </div>
      )}
      {!isConnected && !connectionError && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2">
          <p className="text-xs text-yellow-700">Connecting to chat service...</p>
        </div>
      )}

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <AnimatePresence>
          {messages.map((message, index) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  message.role === 'user'
                    ? 'bg-purple-600 text-white rounded-br-sm'
                    : 'bg-gray-100 text-gray-900 rounded-bl-sm'
                }`}
              >
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                <div className={`text-xs mt-1 ${message.role === 'user' ? 'text-purple-200' : 'text-gray-500'}`}>
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Loading indicator */}
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex justify-start"
          >
            <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-4 py-3">
              <Loader2 className="w-5 h-5 text-gray-600 animate-spin" />
            </div>
          </motion.div>
        )}

        {/* Suggested Questions (show only if first message) */}
        {messages.length === 1 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="space-y-2"
          >
            <p className="text-xs text-gray-500 px-1">Suggested questions:</p>
            {suggestedQuestions.map((question, index) => (
              <button
                key={index}
                onClick={() => {
                  setInput(question);
                  inputRef.current?.focus();
                }}
                className="block w-full text-left px-4 py-2 text-sm text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition"
              >
                {question}
              </button>
            ))}
          </motion.div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="border-t border-gray-200 p-4">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none text-sm"
            disabled={isLoading || !isConnected}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading || !isConnected}
            className="bg-purple-600 hover:bg-purple-700 text-white p-3 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2 text-center">
          Powered by DoubleMe • AI responses may vary • No microphone required
        </p>
      </div>
    </div>
  );
};

export default CustomChatWidget;
